/**
 * Pure descriptive statistics for the U-0 latency harness.
 *
 * No DOM, no Dexie, no clock, no randomness — every input arrives as a parameter. Enforced by
 * `tsconfig.pure.json` and the `nfr-6/pure-layer-boundary` ESLint block.
 *
 * **Why a distribution and not a mean.** A p50 inside NFR-1's budget with a p99 outside it is the
 * exact shape that corrupts a live match: the acknowledgment is trustworthy ninety-nine taps out
 * of a hundred, so the user stops foveating (SC-5), and the hundredth tap is the one they never
 * notice was late. Every consumer of this module gets tail figures or nothing.
 */

export interface Summary {
  n: number;
  min: number;
  p50: number;
  p75: number;
  p90: number;
  p95: number;
  p99: number;
  max: number;
  mean: number;
  /** Population standard deviation. */
  stdDev: number;
}

export interface HistogramBucket {
  /** Inclusive lower edge, milliseconds. */
  fromMs: number;
  /** Exclusive upper edge, milliseconds. `Infinity` on the last bucket. */
  toMs: number;
  count: number;
}

export class StatsInputError extends Error {
  override readonly name = 'StatsInputError';
}

/**
 * Nearest-rank percentile on the sorted sample, `ceil(p/100 * n)`, 1-indexed.
 *
 * Chosen over linear interpolation on purpose: an interpolated p99 invents a latency that was
 * never observed, and the question A-1 asks is whether a commit *did* exceed the budget. Every
 * figure this module reports is a value the device actually produced.
 *
 * @param sorted ascending, finite, non-empty
 * @param p      percentile in (0, 100]
 */
function percentileOfSorted(sorted: readonly number[], p: number): number {
  const rank = Math.ceil((p / 100) * sorted.length);
  const index = Math.min(sorted.length - 1, Math.max(0, rank - 1));
  // `noUncheckedIndexedAccess` is on; the clamp above guarantees the index is in range.
  return sorted[index] as number;
}

function assertUsable(values: readonly number[]): void {
  if (values.length === 0) {
    throw new StatsInputError('summarize: no samples. A verdict from zero trials is not a verdict.');
  }
  for (let i = 0; i < values.length; i++) {
    const v = values[i] as number;
    if (!Number.isFinite(v)) {
      throw new StatsInputError(
        `summarize: sample ${i} is ${String(v)}. A non-finite latency means the timing instrumentation failed; it must not be averaged away.`,
      );
    }
    if (v < 0) {
      throw new StatsInputError(
        `summarize: sample ${i} is negative (${v}). Elapsed time cannot run backwards; this indicates a clock or anchor bug.`,
      );
    }
  }
}

/**
 * @throws {StatsInputError} on an empty sample, a non-finite value, or a negative duration.
 *   Deliberately loud: NFR-8 forbids swallowing these, and a silently-dropped `NaN` would move
 *   the p99 in the safe-looking direction.
 */
export function summarize(values: readonly number[]): Summary {
  assertUsable(values);

  const sorted = [...values].sort((a, b) => a - b);
  const n = sorted.length;

  let sum = 0;
  for (const v of sorted) sum += v;
  const mean = sum / n;

  let squaredDeviation = 0;
  for (const v of sorted) squaredDeviation += (v - mean) ** 2;

  return {
    n,
    min: sorted[0] as number,
    p50: percentileOfSorted(sorted, 50),
    p75: percentileOfSorted(sorted, 75),
    p90: percentileOfSorted(sorted, 90),
    p95: percentileOfSorted(sorted, 95),
    p99: percentileOfSorted(sorted, 99),
    max: sorted[n - 1] as number,
    mean,
    stdDev: Math.sqrt(squaredDeviation / n),
  };
}

/** Count of samples strictly greater than `thresholdMs`. */
export function countOver(values: readonly number[], thresholdMs: number): number {
  if (!Number.isFinite(thresholdMs)) {
    throw new StatsInputError(`countOver: threshold must be finite, received ${String(thresholdMs)}`);
  }
  let over = 0;
  for (const v of values) if (v > thresholdMs) over++;
  return over;
}

/** Fraction (0–1) of samples strictly greater than `thresholdMs`. Empty input yields 0. */
export function fractionOver(values: readonly number[], thresholdMs: number): number {
  if (values.length === 0) return 0;
  return countOver(values, thresholdMs) / values.length;
}

/**
 * Fixed-edge histogram. Edges are supplied by the caller (from `config`), not invented here, so
 * the buckets on the results screen line up with the thresholds the verdict uses.
 *
 * @param edges ascending, finite, non-negative. Produces `edges.length + 1` buckets:
 *   `[0, e0) [e0, e1) … [eLast, ∞)`.
 */
export function histogram(
  values: readonly number[],
  edges: readonly number[],
): HistogramBucket[] {
  for (let i = 0; i < edges.length; i++) {
    const e = edges[i] as number;
    if (!Number.isFinite(e) || e < 0) {
      throw new StatsInputError(`histogram: edge ${i} must be finite and non-negative, got ${String(e)}`);
    }
    if (i > 0 && e <= (edges[i - 1] as number)) {
      throw new StatsInputError(`histogram: edges must ascend strictly; edge ${i} (${e}) does not`);
    }
  }

  const buckets: HistogramBucket[] = [];
  let lower = 0;
  for (const edge of edges) {
    buckets.push({ fromMs: lower, toMs: edge, count: 0 });
    lower = edge;
  }
  buckets.push({ fromMs: lower, toMs: Number.POSITIVE_INFINITY, count: 0 });

  for (const v of values) {
    if (!Number.isFinite(v)) continue; // `summarize` is the loud path; the chart tolerates gaps.
    let placed = false;
    for (const b of buckets) {
      if (v >= b.fromMs && v < b.toMs) {
        b.count++;
        placed = true;
        break;
      }
    }
    if (!placed) (buckets[buckets.length - 1] as HistogramBucket).count++;
  }

  return buckets;
}

/** Rounds to one decimal for display without pretending to precision the timer does not have. */
export function round1(value: number): number {
  return Math.round(value * 10) / 10;
}
